import { getFeature } from '@micro-frontend-react/employee-experience/lib/UsageTelemetryHelper';
import { Spinner } from 'office-ui-fabric-react';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { Label } from 'office-ui-fabric-react/lib/Label';
import * as React from 'react';
import { useState } from 'react';
import {
  HomeLettersContainer,
  LetterChevron,
  LetterTextContainer,
  LetterTypeContainer,
  LetterTypeDescription,
  LetterTypeTitle,
  LetterTypeTitleContainer,
  PageContainer,
  PageHeading,
  PageSubHeading,
  PageTitle,
} from './Home.styled';
import { Link } from './Share.styled';
import { ILetterTemplate } from '../models/ILetterTemplate';
import { IUserProfile } from '../models/IUserProfile';
import { useLetterTemplates } from '../redux/hooks/useLetterTemplates';
import { useUserProfile } from '../redux/hooks/userUserProfile';
import { PdfPanel } from './Panels//PdfView/PdfPanel';
import { EmailedGenerationPanel } from './Panels/EmailedGeneration/EmailedGenerationPanel';
import { Impersonation } from './Sections/Impersonation/Impersonation';
import { MailedGenerationPanel } from './Panels/MailedGeneration/MailedGenerationPanel';
import { MetadataPanel } from './Panels/Metadata/MetadataPanel';
import { SuccessfulGenerationPanel } from './Panels/SuccessfulGeneration/SuccessfulGenerationPanel';
import * as styled from './Home.styled';
import { IMetadataContext, MetadataContext } from '../shared/MetadataContext';
import { ILetterSubmissionRequest } from '../models/ILetterGenerationRequest';
import {
  EventType,
  UsageEventName,
  UserEvent,
} from '@micro-frontend-react/employee-experience/lib/UsageTelemetry';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { requestMetadata } from '../redux/actions/metadataActions';
import { isLetterGenerationRestricted } from '../Utils/isLetterGenerationRestricted';

export function Home(): React.ReactElement {
  const { useSelector, dispatch } = React.useContext(
    Context as React.Context<IEmployeeExperienceContext>
  );
  const currentImpersonation = useSelector((state: any): IUserProfile => {
    return {
      alias: state?.userImpersonation.alias,
      allowedAva: state?.userImpersonation.allowedAva,
      allowedImpersonation: state?.userImpersonation.allowedImpersonation,
      companyCode: state?.userImpersonation.companyCode,
      costCenter: state?.userImpersonation.costCenter,
    };
  });
  const [selectedLetter, selectedLetterChanged] = useState<ILetterTemplate>(null);
  const { telemetryClient } = React.useContext(
    Context as React.Context<IEmployeeExperienceContext>
  );
  const feature = getFeature('EVL', 'Home');
  const [userProfile, isUserProfileLoading, userProfileHasError, userProfileErrorMessage] =
    useUserProfile(feature);

  const toggleMetadataPanel = (valueMetadata: boolean) => {
    _setMetadataContext((p) => ({ ...p, showMetadataPanel: valueMetadata }));
  };

  const togglePdfPanel = (valuePdf: boolean) => {
    _setMetadataContext((p) => ({ ...p, showPdfPanel: valuePdf }));
  };

  const toggleSuccessGenerationPanel = (value: boolean) => {
    _setMetadataContext((p) => ({ ...p, showSuccessGenerationPanel: value }));
  };

  const toggleEmailedGenerationPanel = (value: boolean) => {
    _setMetadataContext((p) => ({ ...p, showEmailedGenerationPanel: value }));
  };

  const toggleMailedGenerationPanel = (value: boolean) => {
    _setMetadataContext((p) => ({ ...p, showMailedGenerationPanel: value }));
  };

  const setUserProfile = (value: IUserProfile) => {
    _setMetadataContext((p) => ({ ...p, userProfile: value }));
  };

  const setSubmissionRequest = (value: ILetterSubmissionRequest) => {
    _setMetadataContext((p) => ({
      ...p,
      submissionRequest: value,
    }));
  };

  const clearSelectedLetter = () => {
    selectedLetterChanged(null);
  };

  const initState: IMetadataContext = {
    showMetadataPanel: false,
    showPdfPanel: false,
    showSuccessGenerationPanel: false,
    showEmailedGenerationPanel: false,
    showMailedGenerationPanel: false,
    submissionRequest: null,
    userProfile: null,
    toggleMetadataPanel: toggleMetadataPanel,
    togglePdfPanel: togglePdfPanel,
    toggleSuccessGenerationPanel: toggleSuccessGenerationPanel,
    toggleEmailedGenerationPanel: toggleEmailedGenerationPanel,
    toggleMailedGenerationPanel: toggleMailedGenerationPanel,
    setSubmissionRequest: setSubmissionRequest,
    clearSelectedLetter: clearSelectedLetter,
    setUserProfile: setUserProfile,
  };

  React.useEffect(() => {
    if (userProfile != null) setUserProfile(userProfile);
  }, [userProfile]);

  const [_metadataContext, _setMetadataContext] = useState<IMetadataContext>(initState);

  const [templates, isLoading, hasError, errorMessage, statusCode] = useLetterTemplates(
    feature,
    currentImpersonation?.companyCode,
    currentImpersonation?.costCenter
  );

  const linkEvent: UserEvent = {
    feature: 'EVL',
    subFeature: 'Home.MainContent',
    subFeatureLevel2: '',
    featureLocation: 'Home',
    eventName: UsageEventName.PageLoad,
    type: EventType.User,
  };

  telemetryClient.trackEvent(linkEvent);

  const essBannerCompanyCodes = __ESS_BANNER_COMPANY_CODES__
    ? __ESS_BANNER_COMPANY_CODES__.split(',').map((c: string) => c.trim())
    : [];
  const showEssBanner =
    essBannerCompanyCodes.length > 0 && userProfile?.companyCode &&
    essBannerCompanyCodes.includes(userProfile.companyCode);
  const effectiveCompanyCode = currentImpersonation?.companyCode || userProfile?.companyCode;
  const generationIsRestricted = isLetterGenerationRestricted(
    effectiveCompanyCode,
    selectedLetter?.name,
    __EVL_STOP_COMPANY_CODES__,
    __EVL_STOP_TEMPLATE_NAMES__
  );

  return (
    <>
      {showEssBanner && (
        <MessageBar
          isMultiline={true}
          styles={{
            root: { width: '100%', backgroundColor: '#e6f2ff', justifyContent: 'center' },
            iconContainer: { display: 'none' },
            text: { fontSize: 14, lineHeight: '22px', textAlign: 'center', width: '100%' },
            innerText: { textAlign: 'center', width: '100%' },
          }}
        >
            <span aria-hidden="true">📢</span> Hello There! Employment Proof, Residential Address Proof, Salary Proof Letters are
            now available through ESS for your region.
            <br />
            Please check out{' '}
            <a href={__ESS_URL__} target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4', fontWeight: 600, textDecoration: 'underline' }}>
              Employee Self-Service Agent
            </a>{' '}
            <span aria-hidden="true">🚀</span> and use prompts like &quot;Generate Employment Verification letter&quot;.
        </MessageBar>
      )}
      <styled.Page>
        <PageContainer>
          {!isUserProfileLoading && userProfile?.allowedImpersonation && (
            <Impersonation userProfile={userProfile} />
          )}
          {isLoading && (
            <styled.HomeStatusContainer>
              <Spinner label="" />
              <Label style={{ fontSize: 20 }}>Loading your content...</Label>
            </styled.HomeStatusContainer>
          )}
          {!isLoading && hasError && (
            <styled.HomeStatusContainer>
              <div role="region" aria-live="polite">
                {
                  {
                    500: (
                      <>
                        <styled.ErrorLoadingTemplates
                          src={`./assets/NotFoundLogo.png`}
                          role="presentation"
                        />
                        <Label>Oops! Something went wrong.</Label>
                      </>
                    ),
                    404: (
                      <>
                        <styled.ErrorLoadingTemplates
                          src={`./assets/NoTemplatesLogo.png`}
                          role="presentation"
                        />
                        <Label>There are no letters available for your company code.</Label>
                      </>
                    ),
                    401: (
                      <>
                        <styled.ErrorLoadingTemplates
                          src={`./assets/UnauthorizedLogo.png`}
                          role="presentation"
                        />
                        <h3>Sorry, you don&apos;t have permissions to this page.</h3>
                      </>
                    ),
                    403: (
                      <>
                        <styled.ErrorLoadingTemplates
                          src={`./assets/UnauthorizedLogo.png`}
                          role="presentation"
                        />
                        <h3>Sorry, you don&apos;t have permissions to this page.</h3>
                      </>
                    ),
                  }[statusCode]
                }
              </div>
            </styled.HomeStatusContainer>
          )}
          {!isLoading && !hasError && (
            <>
              <PageHeading>Employment Verification Letter</PageHeading>
              <PageSubHeading>
                Request a letter from Microsoft that verifies your employment, salary, or other
                information that you need to supply to companies or institutions.
                {<br />}{<br />}You can use this tool to download and print the letter. 
                {<br />}{<br />}Should you need a customized letter with non-standard language, please send your request along with a copy of the standard letter to AskHR for further processing. Note that customized letter requests are subject to HR review and provided on a case-by-case basis in accordance with internal policies.
                {<br />}{<br />}Use Microsoft Edge to view and print your letter.
                {<br />}{<br />}
                {<b>{'Before you begin:'}</b>} To ensure that your personal information is displayed
                correctly in your letter, go to{' '}
                <Link
                  to={__Employee_Central_Link__}
                  title="Employee Central"
                  target="_blank"
                  usageEvent={{
                    feature: 'EVL',
                    subFeature: 'Home.MainContent.EmployeeCentralLink',
                  }}
                >
                  Employee Central (EC)
                </Link>{' '}
                &gt; My Profile.
              </PageSubHeading>

              <PageTitle>Select the letter you need.</PageTitle>

              <HomeLettersContainer>
                {templates?.map(
                  (i: ILetterTemplate): React.ReactElement => (
                    <>
                      <LetterTypeContainer
                        key={i.id}
                        aria-label={`Letter ${i.name} ${i.description}`}
                        role={'button'}
                        tabIndex={0}
                        onKeyPress={(event) => {
                          if (event.key === 'Enter') {
                            const _selectedLetter = templates.find((f) => f.id === i.id);

                            const linkEvent: UserEvent = {
                              feature: 'EVL',
                              subFeature: 'Home.MainContent.Letters',
                              subFeatureLevel2: '',
                              featureLocation: 'Home',
                              eventName: 'KeyPressedEnter',
                              type: EventType.User,
                            };

                            telemetryClient.trackEvent(linkEvent, {
                              SelectedLetterId: _selectedLetter.id,
                              SelectedLetterName: _selectedLetter.name,
                            });

                            selectedLetterChanged(_selectedLetter);
                            dispatch(requestMetadata(feature, _selectedLetter));
                            _setMetadataContext({
                              ..._metadataContext,
                              showMetadataPanel: true,
                            });
                          }
                        }}
                        onClick={() => {
                          const _selectedLetter = templates.find((f) => f.id === i.id);
                          const linkEvent: UserEvent = {
                            feature: 'EVL',
                            subFeature: 'Home.MainContent.Letters',
                            subFeatureLevel2: '',
                            featureLocation: 'Home',
                            eventName: UsageEventName.LinkClicked,
                            type: EventType.User,
                          };

                          telemetryClient.trackEvent(linkEvent, {
                            SelectedLetterId: _selectedLetter.id,
                            SelectedLetterName: _selectedLetter.name,
                          });

                          selectedLetterChanged(_selectedLetter);
                          dispatch(requestMetadata(feature, _selectedLetter));
                          _setMetadataContext({
                            ..._metadataContext,
                            showMetadataPanel: true,
                          });
                        }}
                      >
                        <LetterTextContainer>
                          <LetterTypeTitleContainer>

                            <LetterTypeTitle aria-hidden={true}>
                              {i.name}
                            </LetterTypeTitle>

                            <LetterTypeDescription aria-hidden={true}>
                              {i.description}
                            </LetterTypeDescription>

                          </LetterTypeTitleContainer>
                          <LetterChevron
                            src={`./assets/chevron.png`}
                            alt="No chevron"
                            aria-hidden={true}
                            width={8}
                            height={15}
                          />
                        </LetterTextContainer>
                      </LetterTypeContainer>
                    </>
                  )
                )}
              </HomeLettersContainer>
            </>
          )}
        </PageContainer>
      </styled.Page>

      <MetadataContext.Provider value={_metadataContext}>
        <MetadataPanel
          selectedLetter={selectedLetter}
          isGenerationRestricted={generationIsRestricted}
        />
        <PdfPanel selectedLetter={selectedLetter} />
        <SuccessfulGenerationPanel selectedLetter={selectedLetter} />
        <EmailedGenerationPanel selectedLetter={selectedLetter} />
        <MailedGenerationPanel selectedLetter={selectedLetter} />
      </MetadataContext.Provider>
    </>
  );
}
